/**
 * PortafolioPage
 * Vista consolidada del portafolio: calidad, tamaño, rentabilidad, cohortes, acción.
 * Reemplaza PortfolioDashboardV2Page + PortfolioHistoricoPage en una sola pantalla.
 */

import { useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { ROUTES } from '@/constants';
import { useBackofficeCredits } from '@/features/credit/hooks';
import { usePortfolioV2 } from '@/features/portfolio/hooks/usePortfolioV2';
import {
  scoreColor,
  computeOverdueAmount,
  computeHealthGlobal,
} from '@/features/credit/utils/creditHealth';
import type { BackofficeCreditsListItem } from '@/types/backoffice-crud.types';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Portafolio', path: ROUTES.PORTFOLIO_DASHBOARD },
];

const MES_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function formatM(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
  return `$${v.toFixed(0)}`;
}

function formatDeltaShort(v: number): string {
  const abs = Math.abs(v);
  const prefix = v >= 0 ? '+' : '−';
  if (abs >= 1_000_000) return `${prefix}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${prefix}${Math.round(abs / 1_000)}k`;
  return `${prefix}${Math.round(abs)}`;
}

// ─── Main ─────────────────────────────────────────────────────────────────

export function PortafolioPage() {
  const { credits, isLoading: creditsLoading } = useBackofficeCredits();
  const portfolio = usePortfolioV2();
  const isLoading = creditsLoading || portfolio.isLoading;

  const aliveCredits = useMemo(
    () => credits.filter((c) => {
      const s = c.status?.toUpperCase();
      return s === 'ACTIVE' || s === 'DELINQUENT';
    }),
    [credits],
  );

  const allCredits = credits;

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Portafolio" subtitle="Calidad · Tamaño · Rentabilidad" breadcrumbs={BREADCRUMBS} />
        <div className="space-y-4">
          <Skeleton height={180} variant="rectangular" />
          <Skeleton height={300} variant="rectangular" />
          <Skeleton height={300} variant="rectangular" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Portafolio"
        subtitle="Calidad · Tamaño · Rentabilidad · Cohortes"
        breadcrumbs={BREADCRUMBS}
        actions={
          <Button onClick={portfolio.refetch}>
            <RefreshCw size={16} />
            Actualizar
          </Button>
        }
      />

      <div className="space-y-4">
        <CalidadHero alive={aliveCredits} all={allCredits} />

        {/* Cartera + Rentabilidad lado a lado */}
        <div className="grid gap-4 lg:grid-cols-2">
          <SaldoNetoSection timeline={portfolio.timeline} />
          <RentabilidadSection timeline={portfolio.timeline} />
        </div>

        {/* Cohortes — heatmap + composición lado a lado */}
        <div className="grid gap-4 lg:grid-cols-2">
          <VintageHeatmapSection credits={allCredits} />
          <VintageCompositionSection credits={allCredits} />
        </div>
      </div>
    </div>
  );
}

// ─── 1. Calidad Hero (2 paneles) ──────────────────────────────────────────

function CalidadHero({ alive, all }: { alive: BackofficeCreditsListItem[]; all: BackofficeCreditsListItem[] }) {
  const healthActual = computeHealthGlobal(alive);
  const healthHistorico = computeHealthGlobal(all);

  const overdueAmount = alive.reduce((s, c) => s + computeOverdueAmount(c), 0);
  const aliveBalance = alive.reduce((s, c) => {
    const remaining = (c.weeks_total ?? c.term_weeks ?? 0) - (c.weeks_paid ?? 0);
    return s + Math.max(0, remaining * (c.weekly_payment_mxn ?? 0));
  }, 0);
  const moraRate = aliveBalance > 0 ? (overdueAmount / aliveBalance) * 100 : 0;
  const inDefaultAlive = alive.filter((c) => c.status?.toUpperCase() === 'DELINQUENT').length;

  const totalCount = all.length;
  const defaultedCount = all.filter((c) => {
    const s = c.status?.toUpperCase();
    return s === 'DEFAULTED' || s === 'CANCELLED';
  }).length;
  const defaultRate = totalCount > 0 ? (defaultedCount / totalCount) * 100 : 0;
  const paidRate = totalCount > 0 ? ((totalCount - defaultedCount) / totalCount) * 100 : 0;
  const totalDisbursed = all.reduce((s, c) => s + (c.amount_mxn ?? 0), 0);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <CalidadCard
        title="Calidad actual"
        meta={`cartera viva · ${alive.length} ${alive.length === 1 ? 'crédito' : 'créditos'}`}
        score={healthActual}
        accent="blue"
        kpis={[
          { label: 'Tasa de mora', value: `${moraRate.toFixed(1)}%`, sub: `${formatM(overdueAmount)} vencidos`, tone: moraRate > 10 ? 'danger' : 'warn' },
          { label: 'En atraso', value: String(inDefaultAlive), sub: `de ${alive.length} vivos`, tone: inDefaultAlive > 0 ? 'warn' : 'good' },
          { label: 'Saldo vivo', value: formatM(aliveBalance), sub: 'por cobrar', tone: 'good' },
        ]}
      />
      <CalidadCard
        title="Calidad histórica"
        meta={`todo lo prestado · ${all.length} ${all.length === 1 ? 'crédito' : 'créditos'} originados`}
        score={healthHistorico}
        accent="purple"
        kpis={[
          { label: 'Tasa de default', value: `${defaultRate.toFixed(1)}%`, sub: `${defaultedCount} de ${totalCount} históricos`, tone: defaultRate > 5 ? 'danger' : 'warn' },
          { label: 'Tasa de pago', value: `${paidRate.toFixed(1)}%`, sub: `${totalCount - defaultedCount} sin default`, tone: 'good' },
          { label: 'Total prestado', value: formatM(totalDisbursed), sub: 'acumulado', tone: 'good' },
        ]}
      />
    </div>
  );
}

interface CalidadKpi { label: string; value: string; sub: string; tone: 'good' | 'warn' | 'danger' }

function CalidadCard({
  title, meta, score, accent, kpis,
}: {
  title: string;
  meta: string;
  score: number;
  accent: 'blue' | 'purple';
  kpis: CalidadKpi[];
}) {
  const color = scoreColor(score);
  const stateLabel =
    score >= 80 ? 'Sólida' : score >= 60 ? 'Aceptable' : score >= 40 ? 'En atención' : 'Deteriorada';
  const stateClass =
    score >= 80 ? 'bg-green-500/15 text-green-500'
    : score >= 60 ? 'bg-lime-500/15 text-lime-500'
    : score >= 40 ? 'bg-yellow-500/15 text-yellow-500'
    : 'bg-red-500/15 text-red-500';
  const accentBorder = accent === 'blue' ? 'border-l-blue-500' : 'border-l-purple-500';

  return (
    <div className={`rounded-md border border-border bg-card p-4 border-l-[3px] ${accentBorder}`}>
      <div className="mb-3 flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
          <p className="mt-0.5 text-[10px] text-muted-foreground">{meta}</p>
        </div>
        <span className={`rounded px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${stateClass}`}>
          {stateLabel}
        </span>
      </div>

      <div className="mb-4 flex items-center gap-4">
        <ScoreGauge value={score} />
        <div>
          <p className={`text-base font-bold ${color.text}`}>Salud crediticia</p>
          <p className="text-[11px] text-muted-foreground">
            {accent === 'blue' ? 'Promedio ponderado de los créditos vivos' : 'Ponderada por monto de TODO lo prestado'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {kpis.map((k) => (
          <KpiCell key={k.label} {...k} />
        ))}
      </div>
    </div>
  );
}

function KpiCell({ label, value, sub, tone }: CalidadKpi) {
  const colorClass =
    tone === 'good' ? 'text-green-500' : tone === 'warn' ? 'text-yellow-400' : 'text-red-400';
  return (
    <div className="rounded-md bg-muted/30 p-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-lg font-bold tabular-nums ${colorClass}`}>{value}</p>
      <p className="text-[9px] text-muted-foreground">{sub}</p>
    </div>
  );
}

function ScoreGauge({ value }: { value: number }) {
  const clamped = Math.min(100, Math.max(0, value));
  const r = 36;
  const circ = 2 * Math.PI * r;
  const offset = circ - (clamped / 100) * circ;
  const color =
    clamped >= 80 ? '#22c55e' : clamped >= 60 ? '#84cc16' : clamped >= 40 ? '#eab308' : '#ef4444';
  return (
    <div className="relative h-[80px] w-[80px] shrink-0">
      <svg width="80" height="80" viewBox="0 0 80 80" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="40" cy="40" r={r} fill="none" stroke="var(--color-muted, #334155)" strokeWidth="6" />
        <circle
          cx="40" cy="40" r={r} fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 700ms ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-extrabold tabular-nums" style={{ color }}>{Math.round(clamped)}</span>
        <span className="text-[8px] uppercase tracking-wider text-muted-foreground">de 100</span>
      </div>
    </div>
  );
}

// ─── 2. Cartera y cambio mensual (línea saldo + barras originación/cobranza)

interface PortfolioTimelineItem {
  month: string;
  closing_balance_mxn: number;
  disbursed_mxn: number;
  collected_mxn: number;
}

function SaldoNetoSection({ timeline }: { timeline: PortfolioTimelineItem[] }) {
  const series = useMemo(() => {
    const sorted = [...(timeline ?? [])].sort((a, b) => (a.month ?? '').localeCompare(b.month ?? ''));
    return sorted.slice(-6).map((m) => ({
      month: m.month,
      label: monthLabelFromIso(m.month),
      balance: m.closing_balance_mxn ?? 0,
      disbursed: m.disbursed_mxn ?? 0,
      collected: m.collected_mxn ?? 0,
    }));
  }, [timeline]);

  if (series.length === 0) {
    return (
      <Card>
        <p className="text-sm text-muted-foreground">Sin datos históricos de saldo.</p>
      </Card>
    );
  }

  const last = series[series.length - 1]!;
  const prev = series.length > 1 ? series[series.length - 2]! : null;
  const monthDelta = last.balance - (prev?.balance ?? 0);
  const monthNet = last.disbursed - last.collected; // Neto del mes (positivo = crecio la cartera)

  // Escalas SVG línea (balance)
  const maxBalance = Math.max(...series.map((s) => s.balance), 1);
  const minBalance = Math.min(0, ...series.map((s) => s.balance));
  const balanceRange = Math.max(maxBalance - minBalance, 1);
  const yAt = (v: number) => 160 - ((v - minBalance) / balanceRange) * 130;

  const pathPoints = series.map((s, i) => {
    const x = 50 + (i / Math.max(series.length - 1, 1)) * 540;
    return `${x},${yAt(s.balance)}`;
  }).join(' ');
  const areaPoints =
    `M ${50},${yAt(series[0]!.balance)} ` +
    series.slice(1).map((s, i) => {
      const x = 50 + ((i + 1) / Math.max(series.length - 1, 1)) * 540;
      return `L ${x},${yAt(s.balance)}`;
    }).join(' ') +
    ` L 590,180 L 50,180 Z`;

  // Escala barras (originacion arriba, cobranza abajo) — comparten escala para
  // que se puedan comparar visualmente en el mismo eje.
  const maxAbsFlow = Math.max(
    ...series.map((s) => Math.max(s.disbursed, s.collected)),
    1,
  );

  return (
    <Card>
      <div className="mb-2 flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Cartera y cambio mensual
          </p>
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            saldo pendiente + creditos nuevos (verde) y cobros (rojo) del mes
          </p>
        </div>
        <div className="flex items-start gap-5">
          <div className="text-right">
            <p className="text-[10px] text-muted-foreground">Saldo hoy</p>
            <p className="text-2xl font-extrabold tabular-nums text-foreground">{formatM(last.balance)}</p>
            <p className={`text-[11px] font-semibold ${monthDelta >= 0 ? 'text-green-500' : 'text-red-400'}`}>
              {formatDeltaShort(monthDelta)} vs mes previo
            </p>
          </div>
          <div className="border-l border-border pl-4 text-right">
            <p className="text-[10px] text-muted-foreground">Movimiento este mes</p>
            <p className="text-sm font-semibold tabular-nums">
              <span className="text-green-500">+{formatM(last.disbursed)}</span>
              <span className="mx-1 text-muted-foreground">/</span>
              <span className="text-red-400">-{formatM(last.collected)}</span>
            </p>
            <p className={`text-[11px] font-semibold ${monthNet >= 0 ? 'text-green-500' : 'text-red-400'}`}>
              neto {formatDeltaShort(monthNet)}
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-md bg-[#0f172a] px-3 pb-2 pt-3.5">
        {/* Línea de cartera (saldo pendiente al cierre de cada mes) */}
        <div className="relative h-[180px] overflow-hidden">
          <svg viewBox="0 0 600 180" preserveAspectRatio="none" className="block h-full w-full">
            <defs>
              <linearGradient id="saldoGradPort" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
              </linearGradient>
            </defs>
            <line x1="0" y1="40" x2="600" y2="40" stroke="#1e293b" strokeWidth="1" />
            <line x1="0" y1="90" x2="600" y2="90" stroke="#1e293b" strokeWidth="1" />
            <line x1="0" y1="140" x2="600" y2="140" stroke="#1e293b" strokeWidth="1" />
            <path d={areaPoints} fill="url(#saldoGradPort)" />
            <polyline
              points={pathPoints}
              fill="none"
              stroke="#3b82f6"
              strokeWidth="2.5"
              vectorEffect="non-scaling-stroke"
            />
            {series.map((s, i) => {
              const x = 50 + (i / Math.max(series.length - 1, 1)) * 540;
              return (
                <circle
                  key={s.month}
                  cx={x}
                  cy={yAt(s.balance)}
                  r={i === series.length - 1 ? 4 : 3}
                  fill="#3b82f6"
                  stroke={i === series.length - 1 ? '#dbeafe' : 'none'}
                  strokeWidth={i === series.length - 1 ? 1.5 : 0}
                />
              );
            })}
          </svg>
          <div className="pointer-events-none absolute inset-0 text-[9px] text-muted-foreground">
            <span className="absolute left-1 top-[32px]">{formatM(maxBalance)}</span>
            <span className="absolute left-1 top-[82px]">{formatM((maxBalance + minBalance) / 2)}</span>
            <span className="absolute left-1 top-[132px]">{formatM(minBalance)}</span>
            <span className="absolute right-2 top-2 text-[11px] font-bold text-blue-200">{formatM(last.balance)}</span>
          </div>
        </div>

        {/* Labels de originacion (verde) arriba */}
        <div className="relative mt-1.5 h-3.5 px-3">
          {series.map((s, i) => {
            if (s.disbursed <= 0) return null;
            const left = (i / Math.max(series.length - 1, 1)) * 100;
            return (
              <div
                key={`d-${s.month}`}
                className="absolute text-[10px] font-bold text-green-500"
                style={{ left: `${left}%`, transform: 'translateX(-50%)' }}
                title={`Originacion ${s.label}: ${formatM(s.disbursed)}`}
              >
                +{formatM(s.disbursed)}
              </div>
            );
          })}
        </div>

        {/* Barras originacion (verde, arriba) + cobranza (rojo, abajo). Comparten cero y escala. */}
        <div className="relative h-14">
          <svg viewBox="0 0 600 56" preserveAspectRatio="none" className="block h-full w-full">
            <line x1="0" y1="28" x2="600" y2="28" stroke="#475569" strokeWidth="1" strokeDasharray="3,3" />
            {series.map((s, i) => {
              const cx = 50 + (i / Math.max(series.length - 1, 1)) * 540;
              const disbHeight = (s.disbursed / maxAbsFlow) * 22;
              const collHeight = (s.collected / maxAbsFlow) * 22;
              return (
                <g key={`bars-${s.month}`}>
                  {s.disbursed > 0 && (
                    <rect
                      x={cx - 12}
                      y={28 - Math.max(disbHeight, 2)}
                      width={10}
                      height={Math.max(disbHeight, 2)}
                      rx={1.5}
                      fill="#22c55e"
                    >
                      <title>{`Creditos nuevos ${s.label}: ${formatM(s.disbursed)}`}</title>
                    </rect>
                  )}
                  {s.collected > 0 && (
                    <rect
                      x={cx + 2}
                      y={28}
                      width={10}
                      height={Math.max(collHeight, 2)}
                      rx={1.5}
                      fill="#ef4444"
                    >
                      <title>{`Cobros ${s.label}: ${formatM(s.collected)}`}</title>
                    </rect>
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        {/* Labels de cobranza (rojo) abajo */}
        <div className="relative h-3.5 px-3">
          {series.map((s, i) => {
            if (s.collected <= 0) return null;
            const left = (i / Math.max(series.length - 1, 1)) * 100;
            return (
              <div
                key={`c-${s.month}`}
                className="absolute text-[10px] font-bold text-red-400"
                style={{ left: `${left}%`, transform: 'translateX(-50%)' }}
                title={`Cobros ${s.label}: ${formatM(s.collected)}`}
              >
                -{formatM(s.collected)}
              </div>
            );
          })}
        </div>

        {/* Etiquetas de mes */}
        <div className="relative mt-0.5 h-5 px-3">
          {series.map((s, i) => {
            const left = (i / Math.max(series.length - 1, 1)) * 100;
            return (
              <div
                key={`m-${s.month}`}
                className="absolute flex flex-col items-center"
                style={{ left: `${left}%`, transform: 'translateX(-50%)' }}
              >
                <span className="text-[10px] text-muted-foreground">{s.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-3 px-1 text-[11px] text-muted-foreground">
        <LegendDotInline color="bg-blue-500" label="Saldo pendiente" />
        <LegendDotInline color="bg-green-500" label="Creditos nuevos (originacion)" />
        <LegendDotInline color="bg-red-500" label="Cobros del mes" />
      </div>
    </Card>
  );
}

function monthLabelFromIso(iso: string): string {
  if (!iso) return '—';
  const parts = iso.split('-');
  const m = parseInt(parts[1] ?? '1', 10);
  return MES_LABELS[m - 1] ?? iso;
}

// ─── 3. Rentabilidad (línea yield anualizado ponderado + barras intereses) ─

interface PortfolioTimelineItemForYield {
  month: string;
  interest_collected_mxn: number;
  avg_capital_mxn: number;
}

function RentabilidadSection({ timeline }: { timeline: PortfolioTimelineItemForYield[] }) {
  const series = useMemo(() => {
    const sorted = [...(timeline ?? [])].sort((a, b) => (a.month ?? '').localeCompare(b.month ?? ''));
    const last6 = sorted.slice(-6);

    // Yield anualizado ponderado ACUMULADO hasta cada mes.
    // Formula: (sum interest hasta M) / (sum(avg_capital * dias) hasta M) * 365 * 100.
    let acumInterest = 0;
    let acumCapitalDays = 0;
    return last6.map((m) => {
      const interest = m.interest_collected_mxn ?? 0;
      const avgCap = m.avg_capital_mxn ?? 0;
      const daysInMonth = daysInMonthFromIso(m.month);
      acumInterest += interest;
      acumCapitalDays += avgCap * daysInMonth;
      const yieldAnnual = acumCapitalDays > 0 ? (acumInterest / acumCapitalDays) * 365 * 100 : 0;
      return {
        month: m.month,
        label: monthLabelFromIso(m.month),
        interestMonth: interest,
        avgCapital: avgCap,
        yieldAnnualAcum: yieldAnnual,
      };
    });
  }, [timeline]);

  if (series.length === 0) {
    return (
      <Card>
        <p className="text-sm text-muted-foreground">Sin datos para calcular rentabilidad.</p>
      </Card>
    );
  }

  const last = series[series.length - 1]!;
  const totalInteresesCobrados = series.reduce((s, m) => s + m.interestMonth, 0);

  const maxYield = Math.max(...series.map((m) => m.yieldAnnualAcum));
  const minYield = Math.min(0, ...series.map((m) => m.yieldAnnualAcum));
  const yieldRange = Math.max(maxYield - minYield, 1);
  const yAt = (v: number) => 160 - ((v - minYield) / yieldRange) * 130;

  const pathPoints = series.map((m, i) => {
    const x = 50 + (i / Math.max(series.length - 1, 1)) * 540;
    return `${x},${yAt(m.yieldAnnualAcum)}`;
  }).join(' ');

  const maxMargin = Math.max(...series.map((m) => m.interestMonth), 1);

  return (
    <Card>
      <div className="mb-2 flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Rentabilidad de la cartera
          </p>
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            yield anualizado ponderado por capital expuesto (cobros reales)
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-muted-foreground">Rendimiento hoy</p>
          <p className="text-2xl font-extrabold tabular-nums text-amber-400">
            {last.yieldAnnualAcum.toFixed(1)}%
            <span className="ml-1 text-[10px] font-medium text-muted-foreground">anual</span>
          </p>
          <p className="text-[11px] font-semibold text-green-500">
            {formatM(totalInteresesCobrados)} intereses cobrados
          </p>
        </div>
      </div>

      <div className="rounded-md bg-[#0f172a] px-3 pb-2 pt-3.5">
        <div className="relative h-[180px] overflow-hidden">
          <svg viewBox="0 0 600 180" preserveAspectRatio="none" className="block h-full w-full">
            <defs>
              <linearGradient id="roiGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
              </linearGradient>
            </defs>
            <line x1="0" y1="40" x2="600" y2="40" stroke="#1e293b" strokeWidth="1" />
            <line x1="0" y1="90" x2="600" y2="90" stroke="#1e293b" strokeWidth="1" />
            <line x1="0" y1="140" x2="600" y2="140" stroke="#1e293b" strokeWidth="1" />
            <path
              d={`M ${50},${yAt(series[0]!.yieldAnnualAcum)} ${series.slice(1).map((m, i) => {
                const x = 50 + ((i + 1) / Math.max(series.length - 1, 1)) * 540;
                return `L ${x},${yAt(m.yieldAnnualAcum)}`;
              }).join(' ')} L 590,180 L 50,180 Z`}
              fill="url(#roiGrad)"
            />
            <polyline
              points={pathPoints}
              fill="none"
              stroke="#f59e0b"
              strokeWidth="2.5"
              vectorEffect="non-scaling-stroke"
            />
            {series.map((m, i) => {
              const x = 50 + (i / Math.max(series.length - 1, 1)) * 540;
              return (
                <circle
                  key={m.month}
                  cx={x}
                  cy={yAt(m.yieldAnnualAcum)}
                  r={i === series.length - 1 ? 4 : 3}
                  fill="#f59e0b"
                  stroke={i === series.length - 1 ? '#fef3c7' : 'none'}
                  strokeWidth={i === series.length - 1 ? 1.5 : 0}
                />
              );
            })}
          </svg>
          <div className="pointer-events-none absolute inset-0 text-[9px] text-muted-foreground">
            <span className="absolute left-1 top-[32px]">{maxYield.toFixed(1)}%</span>
            <span className="absolute left-1 top-[82px]">{((maxYield + minYield) / 2).toFixed(1)}%</span>
            <span className="absolute left-1 top-[132px]">{minYield.toFixed(1)}%</span>
            <span className="absolute right-2 top-2 text-[11px] font-bold text-amber-200">
              {last.yieldAnnualAcum.toFixed(1)}%
            </span>
          </div>
        </div>

        {/* Etiquetas de intereses cobrados en el mes (arriba) */}
        <div className="relative mt-1.5 h-3.5 px-3">
          {series.map((m, i) => {
            if (m.interestMonth <= 0) return null;
            const left = (i / Math.max(series.length - 1, 1)) * 100;
            return (
              <div
                key={`mg-${m.month}`}
                className="absolute text-[10px] font-bold text-green-500"
                style={{ left: `${left}%`, transform: 'translateX(-50%)' }}
                title={`Intereses cobrados ${m.label}: ${formatM(m.interestMonth)}`}
              >
                {formatM(m.interestMonth)}
              </div>
            );
          })}
        </div>

        {/* Barras: intereses reales cobrados por mes */}
        <div className="relative h-9">
          <svg viewBox="0 0 600 36" preserveAspectRatio="none" className="block h-full w-full">
            <line x1="0" y1="34" x2="600" y2="34" stroke="#475569" strokeWidth="1" strokeDasharray="3,3" />
            {series.map((m, i) => {
              const x = 50 + (i / Math.max(series.length - 1, 1)) * 540 - 10;
              const heightUnit = (m.interestMonth / maxMargin) * 30;
              return (
                <rect
                  key={`bm-${m.month}`}
                  x={x}
                  y={34 - Math.max(heightUnit, m.interestMonth > 0 ? 2 : 0)}
                  width="20"
                  height={Math.max(heightUnit, m.interestMonth > 0 ? 2 : 0)}
                  rx="1.5"
                  fill="#22c55e"
                />
              );
            })}
          </svg>
        </div>

        {/* Mes labels */}
        <div className="relative mt-0.5 h-4 px-3">
          {series.map((m, i) => {
            const left = (i / Math.max(series.length - 1, 1)) * 100;
            return (
              <div
                key={`mm-${m.month}`}
                className="absolute text-[10px] text-muted-foreground"
                style={{ left: `${left}%`, transform: 'translateX(-50%)' }}
              >
                {m.label}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-3 px-1 text-[11px] text-muted-foreground">
        <LegendDotInline color="bg-amber-500" label="Yield anualizado acumulado" />
        <LegendDotInline color="bg-green-500" label="Intereses cobrados del mes" />
      </div>
    </Card>
  );
}

function daysInMonthFromIso(monthIso: string): number {
  // monthIso: "YYYY-MM"
  const parts = monthIso.split('-');
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  if (!y || !m) return 30;
  return new Date(y, m, 0).getDate();
}


// ─── 5a. Vintage Heatmap ──────────────────────────────────────────────────

function VintageHeatmapSection({ credits }: { credits: BackofficeCreditsListItem[] }) {
  const cohorts = useMemo(() => buildVintageCohorts(credits), [credits]);

  if (cohorts.length === 0) {
    return null;
  }

  return (
    <Card>
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Curva de default por cohorte
          </p>
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            % default acumulado por meses transcurridos desde el origen
          </p>
        </div>
        <div className="flex items-center gap-2 text-[9px] text-muted-foreground">
          <span>0%</span>
          <span className="flex h-2 w-20">
            <span className="flex-1" style={{ background: '#14532d' }} />
            <span className="flex-1" style={{ background: '#16a34a' }} />
            <span className="flex-1" style={{ background: '#84cc16' }} />
            <span className="flex-1" style={{ background: '#eab308' }} />
            <span className="flex-1" style={{ background: '#f97316' }} />
            <span className="flex-1" style={{ background: '#ef4444' }} />
          </span>
          <span>15%+</span>
        </div>
      </div>

      <div className="overflow-x-auto rounded-md bg-[#0f172a] p-3">
        <div
          className="grid gap-1 text-[10px]"
          style={{ gridTemplateColumns: '100px repeat(7, minmax(40px, 1fr))' }}
        >
          <div className="px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Cohorte
          </div>
          {[0, 1, 2, 3, 4, 5, 6].map((n) => (
            <div key={n} className="text-center text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              M+{n}
            </div>
          ))}

          {cohorts.map((c) => (
            <VintageRow key={c.month} cohort={c} />
          ))}
        </div>
      </div>

      <p className="mt-2 text-[10px] text-muted-foreground">
        Compara la <b>misma columna</b> entre cohortes — origen reciente vs histórico al mismo punto de vida.
      </p>
    </Card>
  );
}

// ─── 5b. Vintage Composición ──────────────────────────────────────────────

function VintageCompositionSection({ credits }: { credits: BackofficeCreditsListItem[] }) {
  const cohorts = useMemo(() => buildVintageCohorts(credits), [credits]);

  if (cohorts.length === 0) {
    return null;
  }

  return (
    <Card>
      <div className="mb-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Composición y mora actual
        </p>
        <p className="mt-0.5 text-[10px] text-muted-foreground">
          estado FINAL del capital colocado y mora de los aún vivos
        </p>
      </div>

      <div className="overflow-x-auto rounded-md bg-[#0f172a] p-3">
        <div
          className="grid items-center gap-2 text-[11px]"
          style={{ gridTemplateColumns: '100px 1fr 44px 44px 44px 50px' }}
        >
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Cohorte
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Composición
          </div>
          <div className="text-right text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Liq.
          </div>
          <div className="text-right text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Viv.
          </div>
          <div className="text-right text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Def.
          </div>
          <div className="text-right text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Mora
          </div>

          {cohorts.map((c) => (
            <CohortStackedRow key={c.month} cohort={c} />
          ))}
        </div>

        <div className="mt-3 flex flex-wrap gap-3 border-t border-border pt-2 text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-sm bg-green-500" />
            Liquidados
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-sm bg-blue-500" />
            Vivos
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-sm bg-red-500" />
            Default
          </span>
        </div>
      </div>
    </Card>
  );
}

function CohortStackedRow({ cohort }: { cohort: Cohort }) {
  const moraColor =
    cohort.moraPct === 0 ? 'text-muted-foreground'
    : cohort.moraPct < 5 ? 'text-yellow-400'
    : cohort.moraPct < 15 ? 'text-orange-400'
    : 'text-red-400';
  return (
    <>
      <div className="flex flex-col py-1.5 text-[11px]">
        <span className="font-semibold text-foreground">{cohort.label}</span>
        <span className="text-[9px] text-muted-foreground">
          {formatM(cohort.capital)} · {cohort.count} cred
        </span>
      </div>
      <div className="flex h-5 overflow-hidden rounded bg-muted">
        {cohort.paidPct > 0 && <div className="bg-green-500" style={{ width: `${cohort.paidPct}%` }} />}
        {cohort.activePct > 0 && <div className="bg-blue-500" style={{ width: `${cohort.activePct}%` }} />}
        {cohort.defaultPct > 0 && <div className="bg-red-500" style={{ width: `${cohort.defaultPct}%` }} />}
      </div>
      <div className="text-right tabular-nums text-green-500">{cohort.paidPct.toFixed(0)}%</div>
      <div className="text-right tabular-nums text-blue-400">{cohort.activePct.toFixed(0)}%</div>
      <div className={`text-right tabular-nums ${cohort.defaultPct > 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
        {cohort.defaultPct.toFixed(0)}%
      </div>
      <div className={`text-right tabular-nums ${moraColor}`}>
        {cohort.activePct > 0 ? `${cohort.moraPct.toFixed(0)}%` : '—'}
      </div>
    </>
  );
}

interface Cohort {
  month: string;
  label: string;
  capital: number;
  count: number;
  defaultRateByMonth: (number | null)[];
  // Composición final (stacked bars)
  paidPct: number;
  activePct: number;
  defaultPct: number;
  // Tasa de mora actual de los créditos vivos en esta cohorte
  moraPct: number;
}

function buildVintageCohorts(credits: BackofficeCreditsListItem[]): Cohort[] {
  const byMonth = new Map<string, BackofficeCreditsListItem[]>();
  for (const c of credits) {
    if (!c.disbursed_at) continue;
    const m = c.disbursed_at.slice(0, 7);
    const arr = byMonth.get(m) ?? [];
    arr.push(c);
    byMonth.set(m, arr);
  }
  const sortedMonths = Array.from(byMonth.keys()).sort();
  const last7 = sortedMonths.slice(-7);
  const now = new Date();

  return last7.map((month) => {
    const arr = byMonth.get(month) ?? [];
    const capital = arr.reduce((s, c) => s + (c.amount_mxn ?? 0), 0);
    const cohortDate = new Date(`${month}-01T00:00:00Z`);
    const monthsElapsed = Math.min(
      6,
      (now.getUTCFullYear() - cohortDate.getUTCFullYear()) * 12 + (now.getUTCMonth() - cohortDate.getUTCMonth()),
    );
    const defaulted = arr.filter((c) => {
      const s = c.status?.toUpperCase();
      return s === 'DEFAULTED' || s === 'CANCELLED';
    });
    const paid = arr.filter((c) => {
      const s = c.status?.toUpperCase();
      return s === 'PAID_OFF' || s === 'SETTLED';
    });
    const active = arr.filter((c) => {
      const s = c.status?.toUpperCase();
      return s === 'ACTIVE' || s === 'DELINQUENT';
    });
    const moraInActive = active.filter((c) => computeOverdueAmount(c) > 0);

    const defaultRateByMonth: (number | null)[] = [];
    for (let n = 0; n <= 6; n++) {
      if (n > monthsElapsed) {
        defaultRateByMonth.push(null);
      } else {
        const defaultedAtN = defaulted.filter((c) => {
          const weeksPaid = c.weeks_paid ?? 0;
          const monthsToDefault = Math.ceil(weeksPaid / 4.33);
          return monthsToDefault <= n;
        });
        const rate = arr.length > 0 ? (defaultedAtN.length / arr.length) * 100 : 0;
        defaultRateByMonth.push(rate);
      }
    }

    const total = Math.max(arr.length, 1);
    return {
      month,
      label: monthLabelFromIso(month) + ' ' + month.slice(0, 4),
      capital,
      count: arr.length,
      defaultRateByMonth,
      paidPct: (paid.length / total) * 100,
      activePct: (active.length / total) * 100,
      defaultPct: (defaulted.length / total) * 100,
      moraPct: active.length > 0 ? (moraInActive.length / active.length) * 100 : 0,
    };
  });
}

function VintageRow({ cohort }: { cohort: Cohort }) {
  return (
    <>
      <div className="flex flex-col py-1.5 pl-1 text-[11px]">
        <span className="font-semibold text-foreground">{cohort.label}</span>
        <span className="text-[9px] text-muted-foreground">
          {formatM(cohort.capital)} · {cohort.count} cred
        </span>
      </div>
      {cohort.defaultRateByMonth.map((rate, i) => {
        if (rate === null) {
          return (
            <div
              key={i}
              className="flex items-center justify-center rounded text-[10px] text-muted-foreground/40"
            >
              —
            </div>
          );
        }
        const bg = vintageColor(rate);
        const textColor = rate >= 3 && rate < 8 ? '#0f172a' : '#fff';
        return (
          <div
            key={i}
            className="flex items-center justify-center rounded py-1.5 text-[11px] font-bold"
            style={{ background: bg, color: textColor }}
            title={`${rate.toFixed(1)}% default a ${i} meses`}
          >
            {rate.toFixed(0)}%
          </div>
        );
      })}
    </>
  );
}

function vintageColor(rate: number): string {
  if (rate <= 0.5) return '#14532d';
  if (rate <= 3) return '#16a34a';
  if (rate <= 5) return '#84cc16';
  if (rate <= 8) return '#eab308';
  if (rate <= 12) return '#f97316';
  return '#ef4444';
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function LegendDotInline({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-block h-2 w-2 rounded-sm ${color}`} />
      <span>{label}</span>
    </span>
  );
}
